/**
 * importCsv.js
 *
 * Utility functions and constants for parsing and validating CSV files
 * used in the bulk file submission import flow.
 *
 * Mirrors the export side (exportCsv.js, exportZip.js) by isolating all
 * CSV-specific logic away from the modal component, keeping the modal
 * focused solely on UI and Redux orchestration.
 *
 * Key dependencies: none (pure JS, no React/Redux/Chakra imports)
 */

/**
 * Expected CSV headers for file submissions import.
 * Must match the export CSV headers exactly so trainers can re-import the exported file.
 * Writable fields: Score, Result, Remarks, ShowRemarks.
 * Context-only fields (SubQuestName, UserName, Code, etc.) are parsed for preview but ignored on import.
 */
export const EXPECTED_HEADERS = [
  'FileSubmissionSessionId',
  'SubQuestId',
  'SubQuestName',
  'UserId',
  'UserName',
  'Code',
  'ScoreType',
  'TotalScore',
  'PassingScore',
  'FilePaths',
  'Score',
  'Result',
  'Remarks',
  'ShowRemarks',
];

/**
 * Required headers that must be present in the CSV (used as the lookup key on import).
 * The API uses FileSubmissionSessionId to identify which records to update.
 */
export const REQUIRED_HEADERS = ['FileSubmissionSessionId'];

/**
 * Validates CSV headers against expected and required headers.
 * Called immediately after parsing the first row of an uploaded file.
 *
 * @param {string[]} headers - CSV headers extracted from the first row
 * @returns {string[]} Array of error messages; empty array means headers are valid
 */
export const validateHeaders = (headers) => {
  const errors = [];

  // Check for missing required headers
  REQUIRED_HEADERS.forEach((required) => {
    if (!headers.includes(required)) {
      errors.push(`Missing required header: "${required}"`);
    }
  });

  // Check for unexpected headers not in the expected set
  headers.forEach((header) => {
    if (!EXPECTED_HEADERS.includes(header)) {
      errors.push(
        `Unexpected header: "${header}". Expected: ${EXPECTED_HEADERS.join(', ')}`
      );
    }
  });

  return errors;
};

/**
 * Parses CSV text into a 2D array of string values.
 * Implements RFC 4180 CSV standard with support for:
 * - Quoted fields containing commas: "Last, First"
 * - Escaped double-quotes inside quoted fields: "He said ""Hello"""
 * - Multi-line quoted fields: "Line 1\nLine 2"
 * - Both \n (LF) and \r\n (CRLF) line endings
 *
 * @param {string} csvText - Raw CSV file content as a string
 * @returns {string[][]} Array of rows, each row being an array of field strings
 */
export const parseCsvText = (csvText) => {
  const rows = [];
  let currentRow = [];
  let currentField = '';
  let insideQuotes = false;

  for (let i = 0; i < csvText.length; i += 1) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Escaped quote: "" inside a quoted field becomes a literal "
        currentField += '"';
        i += 1; // Skip the second quote
      } else {
        // Toggle quoted-field mode
        insideQuotes = !insideQuotes;
      }
    } else if (char === ',' && !insideQuotes) {
      // Field separator outside quotes — commit the current field
      currentRow.push(currentField.trim());
      currentField = '';
    } else if ((char === '\n' || char === '\r') && !insideQuotes) {
      // Row separator outside quotes — handle both \n and \r\n
      if (char === '\r' && nextChar === '\n') {
        i += 1; // Consume the \n in a \r\n pair
      }
      currentRow.push(currentField.trim());
      // Skip entirely blank rows (e.g. trailing newline)
      if (currentRow.some((field) => field !== '')) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentField = '';
    } else {
      currentField += char;
    }
  }

  // Flush any remaining field/row after the loop (no trailing newline case)
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    if (currentRow.some((field) => field !== '')) {
      rows.push(currentRow);
    }
  }

  return rows;
};

/**
 * Reads a File object as a UTF-8 text string using the browser FileReader API.
 * Wraps the callback-based FileReader in a Promise for use with async/await.
 *
 * @param {File} file - The File object selected by the user (e.g. from an <input type="file">)
 * @returns {Promise<string>} Resolves with the file's text content; rejects on read error
 */
export const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      resolve(event.target.result);
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
};
